/* import __COLOCATED_TEMPLATE__ from './list-descriptors.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { StandaloneTicketAttribute } from 'embercom/objects/standalone/data-fields/common';

export default class extends Component {
  // @Compact is an argument that is used to define if the edit button should appear over the name when hovering the att,
  // or if it should be on the right of the table. It also controls if we open a modal or sidedrawer when editing att.
  @service notificationsService;
  @service appService;
  @service intl;
  @service session;
  @service finStandaloneService;
  @service router;

  @tracked selectedAttribute;
  @tracked showDescriptorDetailsModal = false;
  @tracked showDescriptorArchiveModal = false;
  @tracked selectedType = 'any';
  @tracked selectedTypes = [];

  @tracked currentTab = 'live';
  @tracked conditionsEditorTargetAttribute = null;
  @tracked sideDrawerStartTab = null;

  get standaloneConversationAttributeDescriptors() {
    let standaloneConversationAttributeDescriptors =
      this._displayableStandalonePlatformTicketFields.concat(
        this.liveConversationAttributeDescriptors.filter((attr) => {
          return !this._standalonePlatformTicketFields.some(
            (ticketField) => ticketField.intercom_cvda?.id === attr.id,
          );
        }),
      );

    return standaloneConversationAttributeDescriptors.sortBy('name');
  }

  get _displayableStandalonePlatformTicketFields() {
    return this._standalonePlatformTicketFields
      .filter((ticketField) => {
        return (
          isPresent(ticketField.intercom_cvda) ||
          (ticketField.required && ticketField.id !== 'tags')
        );
      })
      .map((ticketField) => {
        return new StandaloneTicketAttribute({
          standalonePlatform: this._standalonePlatform,
          ticketField,
          intercomCvda: this.liveConversationAttributeDescriptors.find(
            (attr) => attr.id === ticketField.intercom_cvda?.id,
          ),
        });
      });
  }

  get _standalonePlatform() {
    return this.app.standalone_platforms[0].platform_type;
  }

  get _standalonePlatformTicketFields() {
    return this.appService.mainStandalonePlatform.ticketFields;
  }

  get liveConversationAttributeDescriptors() {
    let liveConversationAttributeDescriptors = this.args.conversationAttributeDescriptors
      .rejectBy('archived')
      .reject(this.hasReferenceToUndefinedOrSystemDefinedObjectType);

    return liveConversationAttributeDescriptors.sortBy('order');
  }

  get archivedConversationAttributeDescriptors() {
    return this.args.conversationAttributeDescriptors
      .filterBy('archived')
      .reject(this.hasReferenceToUndefinedOrSystemDefinedObjectType);
  }

  get selectedConversationAttributeDescriptors() {
    if (this.currentTab === 'archived') {
      return this.archivedConversationAttributeDescriptors;
    } else if (this.app.canUseStandalone) {
      return this.standaloneConversationAttributeDescriptors;
    } else {
      return this.liveConversationAttributeDescriptors;
    }
  }

  get availableTypes() {
    let descriptors = this.selectedConversationAttributeDescriptors || [];
    let types = descriptors
      .map((attr) => {
        if (
          (attr.displayableDataType || attr.dataType) === 'List' &&
          attr.isClassificationAttribute
        ) {
          return 'Fin Attributes';
        }
        return attr.displayableDataType || attr.dataType;
      })
      .filter(Boolean)
      .uniq();
    return types;
  }

  get filteredConversationAttributeDescriptors() {
    let filtered = this.selectedConversationAttributeDescriptors;
    if (this.selectedTypes.length) {
      filtered = filtered.filter((attr) => {
        let type = attr.displayableDataType || attr.dataType;
        let isCategory = type === 'List' && attr.isClassificationAttribute;
        let matchesCategory = this.selectedTypes.includes('Fin Attributes') && isCategory;
        let matchesType = this.selectedTypes.includes(type);
        return matchesCategory || matchesType;
      });
    }
    // If nothing is selected, show all
    return filtered;
  }

  get liveTabActive() {
    return this.currentTab === 'live';
  }

  get dateLabel() {
    return this.liveTabActive ? this.columnLabel('created') : this.columnLabel('archived');
  }

  get dateValuePath() {
    return this.liveTabActive ? 'createdAt' : 'archivedAt';
  }

  get app() {
    return this.appService.app;
  }

  get hasEnoughAttributes() {
    return this.liveConversationAttributeDescriptors.filter((attr) => !attr.isBuiltIn).length >= 2;
  }

  columnLabel(key) {
    return this.intl.t(`settings.conversation-attributes.table.${key}`);
  }

  get columns() {
    let isSortable = false;

    let tableColumns = [
      {
        label: this.columnLabel('name'),
        valuePath: 'nameLowercase',
        isSortable,
        minWidth: '200px',
      },
      {
        label: this.columnLabel('format'),
        valuePath: 'dataType',
        isSortable,
      },
    ];
    tableColumns.push({ label: this.dateLabel, valuePath: this.dateValuePath, isSortable });

    if (!this.app.canUseStandalone) {
      if (this.isConversationTicketType) {
        tableColumns.push({
          label: this.columnLabel('visibility'),
          valuePath: 'visibleTo',
          minWidth: '160px',
          width: '350px',
        });
      }

      if (this.liveTabActive) {
        tableColumns = [{ label: '', valuePath: 'order', width: '0' }, ...tableColumns];
      }

      if (this.isConversationTicketType) {
        tableColumns.push({
          label: this.columnLabel('required'),
          valuePath: 'required',
          isSortable,
        });
      } else {
        tableColumns.push({
          label: this.columnLabel('teammate-options'),
          valuePath: 'teammateOptions',
          labelCanWrap: true,
          minWidth: this.args.compact ? '130px' : undefined,
        });
        tableColumns.push({
          label: this.columnLabel('customer-options'),
          valuePath: 'customerOptions',
          labelCanWrap: true,
          minWidth: this.args.compact ? '120px' : undefined,
        });
      }

      tableColumns.push({
        label: this.columnLabel('conditions'),
        valuePath: 'conditionsCount',
        minWidth: '120px',
      });

      if (this.app.canUseCollapseConversationDetails) {
        tableColumns.push({
          label: this.columnLabel('is-always-visible'),
          valuePath: 'isAlwaysVisible',
          isSortable,
        });
      }
    }

    tableColumns.push({
      label: '',
      valuePath: 'actionButtons',
      width: '0',
    });

    return tableColumns;
  }

  get isConversationTicketType() {
    if (this.args.ticketType === undefined) {
      return true;
    }

    return this.args.ticketType.isConversationTicketType;
  }

  get emptyState() {
    return (
      this.args.emptyState || {
        icon: 'conversation',
        title: this.intl.t('settings.data.conversation-attributes.empty-state.title'),
      }
    );
  }

  get standaloneEmptyStateDescription() {
    let integrationUrl = this.router.urlFor('apps.app.settings.standalone.integration');

    // TODO: SA Generic platform migration – Move to platform.componentData
    if (this.appService.app.hasStandalonePlatform('zendesk')) {
      return this.intl.t('standalone.settings.data.conversation-tickets.description.zendesk', {
        integrationUrl,
        htmlSafe: true,
      });
    } else if (this.appService.app.hasStandalonePlatform('salesforce')) {
      return this.intl.t('standalone.settings.data.conversation-tickets.description.salesforce', {
        integrationUrl,
        htmlSafe: true,
      });
    }

    return;
  }

  get categoriesRoute() {
    if (this.app.isStandaloneApp) {
      return 'apps.app.standalone.attributes';
    }
    return 'apps.app.automation.fin-ai-agent.attributes';
  }

  humanReadableCombinedStatus(visible, required) {
    if (visible && required) {
      return 'Visible & required';
    } else if (visible) {
      return 'Visible';
    } else {
      return 'Not visible';
    }
  }

  @action
  editAttribute(attribute) {
    this.sideDrawerStartTab = null;
    this.selectedAttribute = attribute;
    this.showDescriptorDetailsModal = true;
  }

  @action
  editAttributeConditions(attribute) {
    this.sideDrawerStartTab = 'conditions';
    this.selectedAttribute = attribute;
    this.showDescriptorDetailsModal = true;
  }

  @action
  archiveAttribute() {
    this.showDescriptorArchiveModal = true;
  }

  @action
  restoreAttribute(attribute) {
    this.unarchiveAttribute.perform(attribute);
  }

  @action
  switchTab(tab) {
    this.currentTab = tab;
  }

  @action
  selectLiveTab() {
    this.currentTab = 'live';
  }

  @action
  selectArchivedTab() {
    this.currentTab = 'archived';
  }

  @action
  closeModals() {
    this.showDescriptorDetailsModal = false;
    this.showDescriptorArchiveModal = false;
    this.conditionsEditorTargetAttribute = null;
  }

  @action
  saveConditions(attribute) {
    this.doSaveConditions.perform(attribute);
  }

  @action
  onTypeFilterChange(selectedTypes) {
    this.selectedTypes = selectedTypes;
  }

  @task({ drop: true })
  *doSaveConditions(attribute) {
    try {
      yield attribute.conditions.save();
      this.notificationsService.notifyConfirmation(
        this.notification('conditions-success', { attribute: attribute.name }),
      );
      if (this.archivedConversationAttributeDescriptors.length === 0) {
        this.currentTab = 'live';
      }
      this.closeModals();
    } catch (error) {
      let errorMessage = error?.jqXHR?.responseJSON?.errors;
      let errorCodes = error?.jqXHR?.responseJSON?.error_codes;
      if (errorCodes) {
        for (let [field, error] of Object.entries(errorCodes)) {
          let messageKey = `settings.conversation-attributes.notifications.errors.${field}_${error}`;
          if (this.intl.lookup(messageKey)) {
            errorMessage = this.intl.t(messageKey);
          }
        }
      }

      attribute.rollbackAttributes();

      if (errorMessage) {
        this.notificationsService.notifyError(errorMessage);
      } else {
        this.notificationsService.notifyError(
          this.notification('conditions-failed', { attribute: attribute.name }),
        );
      }
    }
  }

  @task({ drop: true })
  *reorder(updatedOrder, draggedWrapper) {
    if (this.liveTabActive) {
      let oldOrder = [...this.liveConversationAttributeDescriptors];

      try {
        let orderChanged = this.updateOrderValues(updatedOrder);
        if (!orderChanged) {
          return;
        }
        yield draggedWrapper.save();

        this.notificationsService.notifyConfirmation(
          this.notification('reorder-success', { attribute: draggedWrapper.name }),
        );
      } catch (error) {
        this.notificationsService.notifyError(
          this.notification('reorder-failed', { attribute: draggedWrapper.name }),
        );
        this.updateOrderValues(oldOrder);
      }
    }
  }

  updateOrderValues(order) {
    let orderChanged = false;
    let numOfDefaultAttributes = this.args.conversationAttributeDescriptors
      .rejectBy('archived')
      .filter((d) => d.isBuiltIn).length;
    // default attributes don't get returned in updatedOrder, that's why we increment the each order by the number of default attributes
    // deafult attributes will always be at the top
    this.liveConversationAttributeDescriptors.forEach((d) => {
      if (order.indexOf(d) >= 0 && d.order !== order.indexOf(d) + numOfDefaultAttributes) {
        d.set('order', order.indexOf(d) + numOfDefaultAttributes);
        orderChanged = true;
      }
    });
    return orderChanged;
  }

  hasReferenceToUndefinedOrSystemDefinedObjectType(attribute, _index, _array) {
    return (
      isPresent(attribute.reference) &&
      (attribute.referencedObjectType === undefined ||
        attribute.referencedObjectType.isSystemDefined)
    );
  }

  @task({ drop: true })
  *unarchiveAttribute(attribute) {
    try {
      attribute.archived = false;
      attribute.order =
        Math.max(...this.liveConversationAttributeDescriptors.map((d) => d.order)) + 1;
      yield attribute.save();
      this.notificationsService.notifyConfirmation(
        this.notification('unarchive-success', { attribute: attribute.name }),
      );
      if (this.archivedConversationAttributeDescriptors.length === 0) {
        this.currentTab = 'live';
      }
    } catch (error) {
      attribute.rollbackAttributes();
      this.notificationsService.notifyError(
        this.notification('unarchive-failed', { attribute: attribute.name }),
      );
    }
  }

  @task({ drop: true })
  *toggleAttributeVisibility(attribute) {
    attribute.isAlwaysVisible = !attribute.isAlwaysVisible;
    yield attribute.save();

    if (attribute.isAlwaysVisible === true) {
      this.notificationsService.notifyConfirmation(
        this.notification('attribute-always-visible', { attribute: attribute.name }),
      );
    } else {
      this.notificationsService.notifyConfirmation(
        this.notification('attribute-hidden', { attribute: attribute.name }),
      );
    }
  }

  notification(translationKey, attributes) {
    return this.intl.t(
      `settings.conversation-attributes.notifications.${translationKey}`,
      attributes,
    );
  }
}
